import type { RewardAnalyticsEvent, RewardOrder } from '~/utils/rewardAnalyticsEvents'
import { rewardEarlyAccessEvent, rewardEventsForNotification, rewardSpendEvent } from '~/utils/rewardAnalyticsEvents'

/** Observe already-loaded private data; analytics never adds a request or a timer. */
export function useRewardLifecycleAnalytics(observeNotifications = true) {
  const rewards = useRewards()
  const analytics = useRewardAnalytics()

  function deliver(event: RewardAnalyticsEvent | null, identity: string, occurredAt: number, owner: string) {
    if (import.meta.server || !event || !analytics.enabled.value || !analytics.since.value || owner !== rewards.customerId.value || !Number.isFinite(occurredAt) || occurredAt < analytics.since.value)
      return
    // IDs stay on this browser, scoped to the customer. Reserve before sending so
    // refetches, remounts and multiple order views cannot count the same fact twice.
    const key = `exalted:rewards-events:${owner}`
    try {
      const stored: unknown = JSON.parse(localStorage.getItem(key) ?? '[]')
      const sent = Array.isArray(stored) ? stored.filter((value): value is string => typeof value === 'string') : []
      const receipt = `${event.name}:${identity}`
      if (sent.includes(receipt))
        return
      localStorage.setItem(key, JSON.stringify([...sent, receipt]))
      analytics.track(event.name, event.parameters)
    }
    catch { /* Optional telemetry never changes rewards or interrupts checkout. */ }
  }

  if (observeNotifications) {
    watch([rewards.scope, analytics.enabled, analytics.since, () => rewards.notifications.value.items], () => {
      const owner = rewards.customerId.value
      if (!owner || !analytics.enabled.value)
        return
      for (const item of rewards.notifications.value.items) {
        for (const event of rewardEventsForNotification(item))
          deliver(event, `notification:${item.id}`, Date.parse(item.createdAt), owner)
      }
    }, { immediate: true })
  }

  function trackOrder(order?: RewardOrder | null) {
    const owner = rewards.customerId.value
    if (!owner || String(order?.customer?.id) !== owner || !order?.orderPlacedAt)
      return
    const occurredAt = Date.parse(order.orderPlacedAt)
    deliver(rewardSpendEvent(order), `order:${order.id}`, occurredAt, owner)
    deliver(rewardEarlyAccessEvent(order), `order:${order.id}`, occurredAt, owner)
  }
  return { trackOrder }
}
